import React, { useEffect, useState } from 'react';

import { makeStyles } from '@material-ui/core/styles';
import {
  ButtonBase,
  Container,
  Divider,
  Paper,
  Typography
} from '@material-ui/core';

import { Button, Icon, Translate, ProgressBar } from '@hydra/common/components';

import { useCopyToClipboard, useI18n } from '@hydra/common/customHooks';
import * as fetcher from '@hydra/utils/fetcher';

const useStyles = makeStyles(theme => ({
  heading: {
    padding: theme.spacing(4, 0)
  },
  subHeading: {
    marginBottom: theme.spacing(4)
  },
  content: {
    padding: theme.spacing(4, 0)
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    marginTop: theme.spacing(1)
  },
  text: {
    flexGrow: 1,
    marginRight: theme.spacing(2)
  },
  buttons: {
    flexGrow: 0
  },
  copyBtn: {
    background: theme.palette.grey[100],
    padding: theme.spacing(1, 2),
    borderRadius: theme.shape.borderRadius * 35,
    flexShrink: 0,
    marginLeft: theme.spacing(1)
  },
  copyText: {
    fontWeight: theme.typography.fontWeightSemibold,
    marginLeft: theme.spacing(1)
  },
  deleteButton: {
    color: theme.palette.error.main,
    marginTop: theme.spacing(1)
  },
  outlinedInput: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: theme.shape.borderRadius * 2.5,
    padding: theme.spacing(1.5),
    maxWidth: 550
  },
  apiLegendContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    flexGrow: 1,
    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row',
      justifyContent: 'space-between'
    },
    paddingBottom: theme.spacing(3)
  },
  apiLegendText: {
    fontSize: theme.typography.fontSizeSmall,
    pointerEvents: 'none'
  }
}));

interface KeysProps {
  data: {
    apiKey?: string | null
    apiUsage: {
      startDate: string
      endDate: string
      remainingHits: number
      allowedHits: number
      usedHits: number
      percentageHits: number
      quotaExceeded: boolean
    }
  }
}

const HIDDEN_API = '•••••••••••••••••••••••••••••••••••••••••••';

export default function ApiKeys ({ data }: KeysProps) {
  const { apiKey: apyKeyData, apiUsage } = data;
  const { translate, locale } = useI18n();
  const classes = useStyles();
  const copy = translate('assistant_tab_copy');
  const copied = translate('assistant_tab_copied');
  const [apiKey, setApiKey] = useState(apyKeyData);
  const [statusText, setStatusText] = useState(copy);
  const [showApiKey, setShowApiKey] = useState(false);
  const [displayedApiKey, setDisplayedApiKey] = useState(HIDDEN_API);
  const { setCopyToClipboard } = useCopyToClipboard();
  const [apiUsageData, setApiUsageData] = useState(apiUsage);

  const handleShowApiKey = () => setShowApiKey(true);
  const handleHideApiKey = () => setShowApiKey(false);

  useEffect(() => setApiUsageData(apiUsage), [apiUsage]);

  const handleDeleteClick = async () => {
    const { apiKey: deletedKey } = await fetcher.del('/api/user/api-key').json();
    setApiKey(deletedKey);
  };

  const handleCreateClick = async () => {
    const { apiKey: newKey } = await fetcher.post('/api/user/api-key').json();
    setApiKey(newKey);
  };

  useEffect(() => {
    if (apiKey) {
      setDisplayedApiKey(showApiKey ? apiKey : HIDDEN_API);
    }
  }, [showApiKey, apiKey]);

  return (
    <>
      <Container maxWidth='lg' className='nr-account-billing'>
        <Typography component='h1' variant='h2' className={classes.heading}>
          <Translate content='account_api_title' />
        </Typography>
      </Container>
      <Divider />
      <Container maxWidth='lg'>
        <div className={classes.content}>
          <Typography component='h2' variant='h3' className={classes.subHeading}>
            <Translate content='keys_key_management' />
          </Typography>
          {apiKey
            ? (
              <>
                <Typography component='h3' variant='h4' paragraph>
                  <Translate content='keys_api_key' />
                </Typography>
                <Paper elevation={0} className={classes.outlinedInput}>
                  {displayedApiKey}
                  <span>
                    {
                      showApiKey
                        ? (
                          <ButtonBase onClick={() => handleHideApiKey()}>
                            <Icon size='sm' type='eye-hide' />
                          </ButtonBase>
                          )
                        : (
                          <ButtonBase onClick={() => {
                            handleShowApiKey();
                            setTimeout(() => handleHideApiKey(), 2000);
                          }}
                          >
                            <Icon size='sm' type='eye-show' />
                          </ButtonBase>
                          )
                      }
                    <Button
                      size='small'
                      variant='contained'
                      onClick={() => {
                        setCopyToClipboard(apiKey);
                        setStatusText(`${copied}!`);
                        setTimeout(() => setStatusText(copy), 2000);
                      }}
                      className={classes.copyBtn}
                    >
                      <Icon type='page-content' size='md' />
                      <span className={classes.copyText}>{statusText}</span>
                    </Button>
                  </span>
                </Paper>
                <Button variant='text' className={classes.deleteButton} onClick={handleDeleteClick}>
                  <Translate content='delete' />
                </Button>
              </>
              )
            : (
              <div className={classes.row}>
                <div className={classes.text}>
                  <Translate content='keys_no_api_key' />
                </div>
                <div className={classes.buttons}>
                  <Button variant='contained' color='primary' onClick={handleCreateClick}>
                    <Translate content='keys_create' />
                  </Button>
                </div>
              </div>
              )}
        </div>
      </Container>
      {
        apiUsageData
          ? (
            <>
              <Divider />
              <Container>
                <div className={classes.content}>
                  <Typography component='h2' variant='h3' className={classes.subHeading}>
                    <Translate content='keys_api_usage_title' />
                  </Typography>
                  <Typography component='h3' variant='h4' paragraph>
                    <strong>{apiUsageData.usedHits}</strong>
                  </Typography>
                  <ProgressBar borderColor='none' value={apiUsageData.usedHits} max={apiUsageData.allowedHits} color='primary' />
                  <div className={classes.row}>
                    <div className={classes.apiLegendContainer}>
                      <Typography className={classes.apiLegendText}>
                        <Translate content='keys_api_usage_total_used' with={{ totalAllowed: apiUsageData.allowedHits }} />
                      </Typography>
                      <Typography className={classes.apiLegendText}>
                        <Translate content='keys_api_usage_percentage_quota_used' with={{ pctQuotaUsed: Math.floor(apiUsageData.percentageHits) }} />
                      </Typography>
                    </div>
                  </div>
                  {
                    apiUsage.quotaExceeded
                      ? (
                        <p>
                          <Button href={`/${locale}/user/plan`} variant='contained' color='primary' size='large'>
                            <Translate content='account_subscription_change_plan' />
                          </Button>
                        </p>
                        )
                      : null
                  }
                  <p>
                    <a href='https://apidocs.woorank.com/' target='_blank' rel='nofollow noopener noreferrer'>
                      <Translate content='read_the_documentation' />
                    </a>
                  </p>
                </div>
              </Container>
            </>
            )
          : null
      }
    </>
  );
}
